import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button, Press } from '@dorixona/ui'
import { formatMoney } from '@dorixona/utils'
import { BackButton } from '../../../ui/BackButton'
import { Icon } from '../../../ui/Icon'
import { Failed, Loading, failureText } from '../../../ui/Load'
import { priceLabel } from '../../../ui/product'
import { useDebounced } from '../../../app/useDebounced'
import { useCart } from '../../cart/CartProvider'
import { MIN_QUERY, useSelectionMatch, useSuggestions, type ItemState } from '../api'
import { canGive, cartLines, skippedCount, type Match } from '../model'
import { useSelection } from '../selection'

/** Пауза после последней буквы, после которой спрашиваем сервер. */
export const SUGGEST_DELAY_MS = 300

/**
 * Поиск нескольких лекарств сразу. Поле сверху, под ним подсказки; нажатие на
 * подсказку кладёт лекарство в «Выбрано», поле очищается и остаётся в фокусе —
 * можно сразу искать следующее (HIG search-fields.md › Consider pairing tokens
 * with search suggestions). Сколько лекарств — без ограничений. Внизу два
 * действия: «Где есть всё» — аптеки, где есть каждое, и «Всё в корзину».
 */
export function MultiSearchScreen() {
  const { t, i18n } = useTranslation(['catalog', 'common'])
  const navigate = useNavigate()
  const cart = useCart()
  const selection = useSelection()
  const field = useRef<HTMLInputElement>(null)
  const [text, setText] = useState('')
  const query = useDebounced(text.trim(), SUGGEST_DELAY_MS)
  const suggestions = useSuggestions(query, i18n.language)
  const stock = useSelectionMatch(selection.items)
  const [adding, setAdding] = useState(false)
  const [cartError, setCartError] = useState<unknown>(null)
  const lines = cartLines(selection.items)
  const skipped = skippedCount(selection.items)
  const chosen = new Set(selection.items.map((item) => item.id))

  // Экран открыли ради поиска — поле сразу в фокусе (search-fields.md › In a
  // search field in a dedicated area, consider immediately focusing the field).
  const focusField = () => {
    ;(field.current as HTMLInputElement).focus()
  }
  useEffect(() => {
    ;(field.current as HTMLInputElement).focus()
  }, [])

  async function addAll() {
    setAdding(true)
    setCartError(null)
    try {
      await cart.addMany(lines)
      void navigate('/cart')
    } catch (error) {
      setCartError(error)
      setAdding(false)
    }
  }

  return (
    <section className="screen ms-screen">
      <div className="mhead">
        <BackButton to="/catalog" />
        <h1>{t('catalog:multiSearch.title')}</h1>
      </div>

      <div className="search ms-field" role="search">
        <Icon name="i-search" size={19} />
        <input
          ref={field}
          type="search"
          enterKeyHint="search"
          value={text}
          placeholder={t('catalog:multiSearch.field')}
          aria-label={t('catalog:multiSearch.field')}
          autoComplete="off"
          onChange={(event) => {
            setText(event.target.value)
          }}
        />
        {text ? (
          <Press
            squish="icon"
            className="ms-clear"
            aria-label={t('catalog:multiSearch.clear')}
            onPress={() => {
              setText('')
              focusField()
            }}
          >
            <Icon name="i-x" size={16} />
          </Press>
        ) : null}
      </div>

      {/* По тексту в поле, а не по запросу: очистили поле — подсказки уходят сразу. */}
      {text.trim().length >= MIN_QUERY ? (
        <div className="ms-suggest">
          <h2 className="visually-hidden">{t('catalog:multiSearch.suggestions')}</h2>
          {suggestions.isPending ? <Loading what={t('common:state.loading')} count={3} /> : null}
          {suggestions.isError ? (
            <Failed
              error={suggestions.error}
              onRetry={() => {
                void suggestions.refetch()
              }}
            />
          ) : null}
          {suggestions.data?.length === 0 ? (
            <div className="card empty">
              <Icon name="i-search" size={54} />
              <div className="t">{t('catalog:nothing')}</div>
            </div>
          ) : null}
          {suggestions.data?.length ? (
            <ul className="card ms-list">
              {suggestions.data.map((product) => {
                const taken = chosen.has(product.id)
                return (
                  <li key={product.id}>
                    <Press
                      squish="row"
                      className="ms-row"
                      disabled={taken}
                      aria-label={t(
                        taken ? 'catalog:multiSearch.added' : 'catalog:multiSearch.add',
                        {
                          name: product.name,
                        },
                      )}
                      onPress={() => {
                        selection.add(product)
                        setText('')
                        // Следующее лекарство — сразу, без лишнего касания поля.
                        focusField()
                      }}
                    >
                      <span className="ms-main">
                        <span className="t1">{product.name}</span>
                        <span className="t2">
                          {[
                            product.maker,
                            priceLabel(product, formatMoney, t('catalog:priceUnknown')),
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      </span>
                      {product.rx ? <span className="pill rx">{t('catalog:rx')}</span> : null}
                      <span className={taken ? 'ms-add on' : 'ms-add'} aria-hidden="true">
                        <Icon name={taken ? 'i-check' : 'i-plus2'} size={18} />
                      </span>
                    </Press>
                  </li>
                )
              })}
            </ul>
          ) : null}
        </div>
      ) : text.trim() ? (
        <p className="ms-hint">{t('catalog:multiSearch.typeMore')}</p>
      ) : selection.items.length === 0 ? (
        <div className="card empty">
          <Icon name="i-pill" size={54} />
          <div className="t">{t('catalog:multiSearch.empty')}</div>
          {t('catalog:multiSearch.hint')}
        </div>
      ) : null}

      {selection.items.length > 0 ? (
        <>
          <h2 className="ms-title">
            {t('catalog:multiSearch.selected')}
            <span className="ms-count num">{selection.items.length}</span>
          </h2>
          <ul className="card ms-list">
            {stock.states.map((state) => (
              <PickedRow
                key={state.item.id}
                state={state}
                onRemove={() => {
                  selection.remove(state.item.id)
                }}
                onQty={(qty) => {
                  selection.setQty(state.item.id, qty)
                }}
              />
            ))}
          </ul>

          <div className="ms-actions">
            <MatchLine pending={stock.pending} result={stock.result} />
            <Button
              onPress={() => {
                void navigate('/search/stores')
              }}
            >
              <Icon name="i-pin" size={18} />
              {t('catalog:multiSearch.whereAll')}
            </Button>
            <Button
              variant="ghost"
              disabled={lines.length === 0}
              loading={adding}
              onPress={() => {
                void addAll()
              }}
            >
              <Icon name="i-cart" size={18} />
              {t('catalog:multiSearch.allToCart')}
            </Button>
            {skipped > 0 ? (
              <p className="ms-note">{t('catalog:multiSearch.skipped', { count: skipped })}</p>
            ) : null}
            {cartError ? (
              <p className="ms-note ms-note--err" role="alert">
                {failureText(cartError, t)}
              </p>
            ) : null}
          </div>
        </>
      ) : null}
    </section>
  )
}

interface PickedRowProps {
  state: ItemState
  onRemove: () => void
  onQty: (qty: number) => void
}

/** Строка «Выбрано»: название, в скольких аптеках есть, количество и «убрать». */
function PickedRow({ state, onRemove, onQty }: PickedRowProps) {
  const { t } = useTranslation(['catalog', 'common'])
  const { item, query } = state

  return (
    <li className="ms-picked">
      <span className="ms-main">
        <span className="t1">
          {item.name}
          {item.rx ? <span className="pill rx">{t('catalog:rx')}</span> : null}
        </span>
        {query.isPending ? (
          <span className="t2">{t('catalog:multiSearch.checking')}</span>
        ) : query.isError ? (
          <span className="t2 ms-err">
            {t('catalog:multiSearch.checkFailed')}
            <Press
              squish="chip"
              className="ms-retry"
              onPress={() => {
                void query.refetch()
              }}
            >
              {t('common:actions.retry')}
            </Press>
          </span>
        ) : (
          <StoresCount stores={query.data.filter((row) => canGive(row, item.qty)).length} />
        )}
      </span>
      <span className="qty ms-qty">
        <Press
          squish="key"
          aria-label={t('catalog:decrease')}
          disabled={item.qty <= 1}
          onPress={() => {
            onQty(item.qty - 1)
          }}
        >
          −
        </Press>
        <span className="num" aria-live="polite">
          {item.qty}
        </span>
        <Press
          squish="key"
          aria-label={t('catalog:increase')}
          onPress={() => {
            onQty(item.qty + 1)
          }}
        >
          +
        </Press>
      </span>
      <Press
        squish="icon"
        className="ms-remove"
        aria-label={t('catalog:multiSearch.remove', { name: item.name })}
        onPress={onRemove}
      >
        <Icon name="i-x" size={16} />
      </Press>
    </li>
  )
}

/** «есть в N аптеках» — или честно, что ни в одной. */
function StoresCount({ stores }: { stores: number }) {
  const { t } = useTranslation('catalog')
  return stores > 0 ? (
    <span className="t2 ms-ok">{t('catalog:multiSearch.inStores', { count: stores })}</span>
  ) : (
    <span className="t2 ms-none">{t('catalog:multiSearch.inNoStore')}</span>
  )
}

interface MatchLineProps {
  pending: boolean
  result: Match | null
}

/** Итог над кнопками: сколько аптек со всем сразу — до того, как нажали. */
function MatchLine({ pending, result }: MatchLineProps) {
  const { t } = useTranslation('catalog')
  if (pending) return <p className="ms-match">{t('catalog:multiSearch.checking')}</p>
  // Наличие не пришло — итога нет: строки выше говорят, у кого и что повторить.
  if (!result) return null
  return result.kind === 'all' ? (
    <p className="ms-match ms-ok" role="status">
      {t('catalog:multiSearch.allHere', { count: result.stores.length })}
    </p>
  ) : (
    <p className="ms-match ms-none" role="status">
      {t('catalog:multiSearch.noneHasAll')}
    </p>
  )
}
